import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { tapLight } from '../lib/haptics';
import './widget-lab.css';

/**
 * /design-lab/widget — unlisted review page for home-screen widget concepts.
 *
 * The Android widget shows "today at a glance" and must never show chore names —
 * just the shape of the day plus a nudge to open the app. Concepts here render as
 * HTML/SVG mock-ups of RemoteViews layouts at the widget's two sizes (4×2 and 2×2)
 * on a fake wallpaper, driven by shared mock day-states.
 *
 * Feasibility ground rules (all concepts respect them):
 * - RemoteViews only: TextView/ImageView/ProgressBar + static bitmaps, no animation.
 * - Anything inside a drawn bitmap (rings, mugs, flames, suns, brand type) is fine —
 *   the current widget already draws its ring this way.
 * - The `overdue` count is a NEW field: today's pushed snapshot is
 *   earned/max/todo/done/skipped/streak, so shipping an overdue-aware concept means
 *   adding `overdue` to `WidgetStatus` (overdue chores are a subset of `todo`).
 */

interface DayState {
  id: string;
  label: string;
  earned: number;
  max: number;
  todo: number;
  done: number;
  skipped: number;
  /** How many of `todo` are overdue — proposed new field, see note above. */
  overdue: number;
  streak: number;
}

const SCENARIOS: DayState[] = [
  { id: 'fresh',   label: 'Fresh morning', earned: 0,  max: 16, todo: 5, done: 0, skipped: 0, overdue: 0, streak: 3 },
  { id: 'rolling', label: 'Mid-day',       earned: 9,  max: 16, todo: 3, done: 3, skipped: 0, overdue: 0, streak: 3 },
  { id: 'oneleft', label: 'One left',      earned: 13, max: 16, todo: 1, done: 4, skipped: 1, overdue: 0, streak: 3 },
  { id: 'goal',    label: 'Goal hit',      earned: 16, max: 16, todo: 0, done: 5, skipped: 0, overdue: 0, streak: 4 },
  { id: 'behind',  label: 'Overdue',       earned: 2,  max: 18, todo: 4, done: 1, skipped: 0, overdue: 2, streak: 0 },
  { id: 'clear',   label: 'Nothing due',   earned: 0,  max: 0,  todo: 0, done: 0, skipped: 0, overdue: 0, streak: 3 },
];

const isComplete = (s: DayState) => s.max > 0 && s.earned >= s.max;
const isClear = (s: DayState) => s.max <= 0;
const progressOf = (s: DayState) => (s.max > 0 ? Math.min(s.earned / s.max, 1) : 0);

/** The one warm line every concept carries — encouraging, never naggy. */
function encourage(s: DayState): string {
  if (isClear(s)) return 'Nothing due today — enjoy it';
  if (isComplete(s)) return 'Goal smashed — take a bow!';
  if (s.todo === 0) return 'All wrapped up for today';
  if (s.overdue > 0) return `${s.overdue} slipped by — easy catch-up`;
  if (s.done === 0) return `${s.todo} little wins up for grabs`;
  if (s.todo === 1) return 'Just one to go — so close!';
  return `${s.done} down, ${s.todo} to go — nice pace`;
}

/** Short form for the 2×2 size. */
function encourageShort(s: DayState): string {
  if (isClear(s)) return 'All clear';
  if (isComplete(s)) return 'Smashed it!';
  if (s.todo === 0) return 'All done';
  return `${s.todo} to go`;
}

/* ---------------- Shared widget furniture ---------------- */

function Wordmark() {
  return (
    <span className="wl-wordmark">
      Chor<i>tl</i>e
    </span>
  );
}

/** Same 24×24 cubics as StreakFlame, but colourable per surface. */
function Flame({ body, core }: { body: string; core: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M12,2.4 C12.6,5.4 14.6,6.8 16.2,9 C17.4,10.7 18,12.7 18,14.8 C18,18.6 15.3,21 12,21 C8.7,21 6,18.6 6,14.8 C6,11.5 8.2,9.4 9.6,6.6 C10.4,5 11.6,4 12,2.4 Z"
        fill={body}
      />
      <path
        d="M12,12 C13.4,13.6 14.2,14.8 14.2,16.4 C14.2,18.1 13,19.2 11.6,19.2 C10.3,19.2 9.3,18.2 9.3,16.8 C9.3,15.2 10.8,14.2 12,12 Z"
        fill={core}
      />
    </svg>
  );
}

function StreakPill({ streak }: { streak: number }) {
  if (streak <= 0) return null;
  return (
    <span className="wl-streak">
      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path
          d="M12,2.4 C12.6,5.4 14.6,6.8 16.2,9 C17.4,10.7 18,12.7 18,14.8 C18,18.6 15.3,21 12,21 C8.7,21 6,18.6 6,14.8 C6,11.5 8.2,9.4 9.6,6.6 C10.4,5 11.6,4 12,2.4 Z"
          fill="currentColor"
        />
      </svg>
      {streak}
    </span>
  );
}

function Card({
  size,
  s,
  paper,
  children,
}: {
  size: '4x2' | '2x2';
  s: DayState;
  paper?: boolean;
  children: ReactNode;
}) {
  const variant = paper ? ' wl-widget--paper' : isComplete(s) ? ' wl-widget--celebrate' : '';
  return <div className={`wl-widget wl-${size}${variant}`}>{children}</div>;
}

function Score({ s, gold }: { s: DayState; gold?: boolean }) {
  return (
    <span className={`wl-pts${gold && isComplete(s) ? ' wl-gold-ink' : ''}`}>
      {s.earned}
      <small>{isClear(s) || isComplete(s) ? ' pts' : ` / ${s.max} pts`}</small>
    </span>
  );
}

/* ================ R · Current (reference rebuild) ================ */

function Ring({ s }: { s: DayState }) {
  const p = progressOf(s);
  const complete = isComplete(s);
  const r = 44;
  const c = 2 * Math.PI * r;
  return (
    <div className="wl-ring">
      <svg viewBox="0 0 100 100">
        <circle cx="50" cy="50" r={r} fill="none" stroke="var(--wl-track)" strokeWidth="10" />
        {p > 0 && (
          <circle
            cx="50" cy="50" r={r} fill="none"
            stroke={complete ? 'var(--wl-gold)' : 'var(--wl-cream)'}
            strokeWidth="10" strokeLinecap="round"
            strokeDasharray={`${p * c} ${c}`}
            transform="rotate(-90 50 50)"
          />
        )}
      </svg>
      <div className="wl-ring-score">
        <Score s={s} gold />
      </div>
    </div>
  );
}

function Counts({ s }: { s: DayState }) {
  return (
    <div className="wl-counts">
      <div className="wl-count"><b>{s.todo}</b><span>To do</span></div>
      <div className="wl-count"><b>{s.done}</b><span>Done</span></div>
      <div className="wl-count"><b>{s.skipped}</b><span>Skipped</span></div>
    </div>
  );
}

function ConceptCurrent({ s }: { s: DayState }) {
  return (
    <>
      <Card size="4x2" s={s}>
        <div className="wl-head"><Wordmark /><StreakPill streak={s.streak} /></div>
        <div className="wl-ring-row">
          <Ring s={s} />
          <Counts s={s} />
        </div>
      </Card>
      <Card size="2x2" s={s}>
        <div className="wl-head"><Wordmark /><StreakPill streak={s.streak} /></div>
        <div className="wl-score-block">
          <Score s={s} gold />
        </div>
        <Counts s={s} />
      </Card>
    </>
  );
}

/* ================ A · Day beads ================ */

function Beads({ s }: { s: DayState }) {
  const beads: ReactNode[] = [];
  const push = (kind: string, key: string) =>
    beads.push(
      <span key={key} className={`wl-bead${kind}`}>
        {kind === ' wl-bead--done' && (
          <svg viewBox="0 0 16 16" fill="none" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 8.5 L6.5 12 L13 4.5" />
          </svg>
        )}
      </span>,
    );
  for (let i = 0; i < s.done; i++) push(' wl-bead--done', `d${i}`);
  for (let i = 0; i < s.overdue; i++) push(' wl-bead--over', `o${i}`);
  for (let i = 0; i < s.todo - s.overdue; i++) push('', `t${i}`);
  for (let i = 0; i < s.skipped; i++) push(' wl-bead--skip', `s${i}`);
  return <div className="wl-beads">{beads}</div>;
}

function ConceptBeads({ s }: { s: DayState }) {
  const clear = isClear(s);
  return (
    <>
      <Card size="4x2" s={s}>
        <div className="wl-head"><Wordmark /><StreakPill streak={s.streak} /></div>
        {clear ? (
          <div className="wl-clear-line">{encourage(s)}</div>
        ) : (
          <>
            <Beads s={s} />
            <div className="wl-beads-foot">
              <span className="wl-line">{encourage(s)}</span>
              <Score s={s} gold />
            </div>
          </>
        )}
      </Card>
      <Card size="2x2" s={s}>
        <div className="wl-head"><Wordmark /><StreakPill streak={s.streak} /></div>
        {clear ? (
          <div className="wl-clear-line">{encourageShort(s)}</div>
        ) : (
          <>
            <Beads s={s} />
            <div className="wl-line">{encourageShort(s)}</div>
          </>
        )}
      </Card>
    </>
  );
}

/* ================ B · Headliner ================ */

function Headline({ s, short }: { s: DayState; short?: boolean }) {
  if (isClear(s)) return <div className="wl-big">All clear</div>;
  if (isComplete(s)) return <div className="wl-big">Done!</div>;
  if (s.todo === 0) return <div className="wl-big">Wrapped</div>;
  return (
    <div className="wl-big">
      {s.todo}
      <small>{short ? 'to go' : s.todo === 1 ? 'chore to go' : 'chores to go'}</small>
    </div>
  );
}

function Bar({ s }: { s: DayState }) {
  return (
    <div className="wl-bar-row">
      <div className="wl-bar">
        <div className="wl-bar-fill" style={{ width: `${progressOf(s) * 100}%` }} />
      </div>
      <Score s={s} gold />
    </div>
  );
}

/** Headliner subline — complements the big count instead of repeating it. */
function subline(s: DayState): string {
  if (isClear(s)) return 'Nothing due today — enjoy it';
  if (isComplete(s)) return 'Goal smashed — take a bow!';
  if (s.todo === 0) return 'All handled — see you tomorrow';
  if (s.overdue > 0) return `${s.overdue} of these slipped by — easy catch-up`;
  if (s.done === 0) return 'Little wins up for grabs';
  if (s.todo === 1) return 'So close — go on!';
  return `${s.done} already down — nice pace`;
}

function ConceptHeadliner({ s }: { s: DayState }) {
  return (
    <>
      <Card size="4x2" s={s}>
        <div className="wl-head"><Wordmark /><StreakPill streak={s.streak} /></div>
        <Headline s={s} />
        <div className="wl-line">{subline(s)}</div>
        {!isClear(s) && <Bar s={s} />}
      </Card>
      <Card size="2x2" s={s}>
        <div className="wl-head"><Wordmark /><StreakPill streak={s.streak} /></div>
        <Headline s={s} short />
        {!isClear(s) && <Bar s={s} />}
      </Card>
    </>
  );
}

/* ================ C · Mug ================ */

function Mug({ s }: { s: DayState }) {
  const p = progressOf(s);
  const complete = isComplete(s);
  // Mug interior: x 14–46, y 20–56 (36 tall). Coffee rises with progress.
  const fillH = 36 * p;
  return (
    <div className="wl-mug">
      <svg viewBox="0 0 64 64" aria-hidden="true">
        {complete && (
          <g stroke="#cb6125" strokeWidth="2.4" strokeLinecap="round" fill="none" opacity="0.75">
            <path d="M24 14 C22 11, 26 9, 24 5" />
            <path d="M34 15 C32 12, 36 10, 34 5" />
          </g>
        )}
        <defs>
          <clipPath id="wl-mug-clip">
            <rect x="14" y="20" width="32" height="36" rx="5" />
          </clipPath>
        </defs>
        {/* handle */}
        <path
          d="M46 28 h5 a7 7 0 0 1 0 14 h-5"
          fill="none" stroke="#3a2415" strokeWidth="3.5"
        />
        {/* body */}
        <rect x="14" y="20" width="32" height="36" rx="5" fill="#fffdf7" stroke="#3a2415" strokeWidth="3.5" />
        {/* coffee */}
        {p > 0 && (
          <g clipPath="url(#wl-mug-clip)">
            <rect x="14" y={56 - fillH} width="32" height={fillH} fill={complete ? '#c8721f' : '#3a2415'} />
            <ellipse cx="30" cy={56 - fillH} rx="16" ry="2.2" fill={complete ? '#e8b84b' : '#5a3a22'} />
          </g>
        )}
        {/* saucer */}
        <path d="M10 59 h40" stroke="#3a2415" strokeWidth="3.5" strokeLinecap="round" />
      </svg>
    </div>
  );
}

function ConceptMug({ s }: { s: DayState }) {
  return (
    <>
      <Card size="4x2" s={s} paper>
        <div className="wl-head"><Wordmark /><StreakPill streak={s.streak} /></div>
        <div className="wl-mug-row">
          <Mug s={s} />
          <div className="wl-mug-side">
            {!isClear(s) && <Score s={s} />}
            <span className="wl-line">{encourage(s)}</span>
          </div>
        </div>
      </Card>
      <Card size="2x2" s={s} paper>
        <div className="wl-head"><Wordmark /></div>
        <div className="wl-mug-row"><Mug s={s} /></div>
        <div className="wl-line">{encourageShort(s)}</div>
      </Card>
    </>
  );
}

/* ================ D · Ember ================ */

function EmberGauge({ s, streakInGap }: { s: DayState; streakInGap?: boolean }) {
  const p = progressOf(s);
  const complete = isComplete(s);
  const anyFire = s.done > 0 || complete;
  return (
    <div className="wl-ember-gauge">
      {/* 270° gauge, open at the bottom; pathLength normalises the dash math. */}
      <svg viewBox="0 0 100 100">
        <path
          d="M 21.72 78.28 A 40 40 0 1 1 78.28 78.28"
          fill="none" stroke="var(--wl-track)" strokeWidth="9" strokeLinecap="round"
        />
        {p > 0 && (
          <path
            d="M 21.72 78.28 A 40 40 0 1 1 78.28 78.28"
            fill="none"
            stroke={complete ? 'var(--wl-gold)' : 'var(--wl-cream)'}
            strokeWidth="9" strokeLinecap="round"
            pathLength={100}
            strokeDasharray={`${p * 100} 100`}
          />
        )}
      </svg>
      <div className="wl-ember-flame" style={{ opacity: anyFire ? 1 : 0.45 }}>
        <Flame body="#3a2415" core={complete ? '#e8b84b' : '#d98a2b'} />
      </div>
      {/* Streak docked in the gauge's open mouth — big enough to read, and
          sitting under the flame so the count and the fire motif read as one. */}
      {streakInGap && s.streak > 0 && (
        <span className="wl-ember-gap-streak">
          <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
              d="M12,2.4 C12.6,5.4 14.6,6.8 16.2,9 C17.4,10.7 18,12.7 18,14.8 C18,18.6 15.3,21 12,21 C8.7,21 6,18.6 6,14.8 C6,11.5 8.2,9.4 9.6,6.6 C10.4,5 11.6,4 12,2.4 Z"
              fill="currentColor"
            />
          </svg>
          {s.streak}
        </span>
      )}
    </div>
  );
}

function ConceptEmber({ s }: { s: DayState }) {
  return (
    <>
      <Card size="4x2" s={s}>
        <div className="wl-head"><Wordmark /></div>
        <div className="wl-ember-row">
          <EmberGauge s={s} />
          <div className="wl-ember-side">
            {!isClear(s) && <Score s={s} gold />}
            {s.streak > 0 && (
              <span className="wl-ember-days">{s.streak}-day streak</span>
            )}
            <span className="wl-line">{encourage(s)}</span>
          </div>
        </div>
      </Card>
      <Card size="2x2" s={s}>
        <div className="wl-head wl-head--center"><Wordmark /></div>
        <div className="wl-ember-row"><EmberGauge s={s} streakInGap /></div>
        <div className="wl-line wl-line--center">{encourageShort(s)}</div>
      </Card>
    </>
  );
}

/* ================ E · Sunrise ================ */

function SunArc({ s, compact }: { s: DayState; compact?: boolean }) {
  const complete = isComplete(s);
  // "Nothing due" parks the sun happily at noon; otherwise it travels with progress.
  const p = isClear(s) ? 0.5 : progressOf(s);
  const gold = complete || isClear(s);
  // Sun rides an ellipse from the left horizon to the right one; the compact
  // size gets its own tighter geometry so the sun stays in frame at any progress.
  const cx = compact ? 70 : 150;
  const rx = compact ? 56 : 130;
  const ry = compact ? 50 : 60;
  const arc = `M ${cx - rx} 70 A ${rx} ${ry} 0 0 1 ${cx + rx} 70`;
  const sx = cx - rx * Math.cos(Math.PI * p);
  const sy = 70 - ry * Math.sin(Math.PI * p);
  const rSun = compact ? 11 : 9;
  return (
    <div className="wl-sun-sky">
      <svg viewBox={compact ? '0 -8 140 88' : '0 -5 300 85'} preserveAspectRatio="xMidYMax meet">
        <path
          d={arc}
          fill="none" stroke="var(--wl-track)" strokeWidth="4"
          strokeLinecap="round" strokeDasharray="0.5 7" pathLength={100}
        />
        {p > 0 && !isClear(s) && (
          <path
            d={arc}
            fill="none"
            stroke={gold ? 'var(--wl-gold)' : 'var(--wl-cream)'}
            strokeWidth="4" strokeLinecap="round"
            pathLength={100}
            strokeDasharray={`${p * 100} 100`}
          />
        )}
        {gold && (
          <g stroke="var(--wl-gold)" strokeWidth="3" strokeLinecap="round">
            {[0, 45, 90, 135, 180, 225, 270, 315].map((deg) => {
              const rad = (deg * Math.PI) / 180;
              return (
                <line
                  key={deg}
                  x1={sx + Math.cos(rad) * (rSun + 4)} y1={sy + Math.sin(rad) * (rSun + 4)}
                  x2={sx + Math.cos(rad) * (rSun + 8)} y2={sy + Math.sin(rad) * (rSun + 8)}
                />
              );
            })}
          </g>
        )}
        <circle cx={sx} cy={sy} r={rSun} fill={gold ? 'var(--wl-gold)' : 'var(--wl-cream)'} />
      </svg>
    </div>
  );
}

function ConceptSunrise({ s }: { s: DayState }) {
  return (
    <>
      <Card size="4x2" s={s}>
        <div className="wl-head"><Wordmark /><StreakPill streak={s.streak} /></div>
        <SunArc s={s} />
        <div className="wl-sun-foot">
          <span className="wl-line">{encourage(s)}</span>
          {!isClear(s) && <Score s={s} gold />}
        </div>
      </Card>
      <Card size="2x2" s={s}>
        <div className="wl-head"><Wordmark /><StreakPill streak={s.streak} /></div>
        <SunArc s={s} compact />
        <div className="wl-line">{encourageShort(s)}</div>
      </Card>
    </>
  );
}

/* ================ Page ================ */

const CONCEPTS: { id: string; title: string; blurb: string; render: (s: DayState) => ReactNode }[] = [
  {
    id: 'current',
    title: 'R · Current (reference)',
    blurb:
      'HTML rebuild of the shipped widget for comparison: points ring + to-do/done/skipped counts, score-as-text at 2×2, gold celebrate state. Informative but count-heavy — nothing invites you in.',
    render: (s) => <ConceptCurrent s={s} />,
  },
  {
    id: 'beads',
    title: 'A · Day beads',
    blurb:
      'The day as a string of beads — one per chore, no names. Filled = done, hollow = to do, gold-ringed = overdue (gentle, not red), dashed = skipped. The shape of the day in one glance, plus one warm line. Beads render as a drawn bitmap; a 12-bead cap with a "+N" bead would handle huge days.',
    render: (s) => <ConceptBeads s={s} />,
  },
  {
    id: 'headliner',
    title: 'B · Headliner',
    blurb:
      'Copy-first: one big display-type number ("3 chores to go"), the warm line, and a slim points bar. The most legible from across a room and the cheapest to build (two TextViews + a ProgressBar); the display type would be a drawn bitmap.',
    render: (s) => <ConceptHeadliner s={s} />,
  },
  {
    id: 'mug',
    title: 'C · Mug',
    blurb:
      'A cream card where an espresso cup fills as points are earned — steam when the goal is hit. The most Chortle-brand idea (chore + chuckle + coffee-break cosiness) and the only light card: worth judging both palettes on wallpaper. Mug is a drawn bitmap.',
    render: (s) => <ConceptMug s={s} />,
  },
  {
    id: 'ember',
    title: 'D · Ember',
    blurb:
      'Streak-forward: the brand flame inside a 270° points gauge. The flame sits dim until the first completion "lights" it; amber core goes gold at the goal, with the streak spelled out beside. At 2×2 the streak count sits in the gauge\'s open mouth, under the flame. Ties the widget to the app\'s fire motif.',
    render: (s) => <ConceptEmber s={s} />,
  },
  {
    id: 'sunrise',
    title: 'E · Sunrise',
    blurb:
      'The day as a sun crossing the sky: the sun rides an arc from morning (left) toward evening as points are earned, turning gold with rays at the goal. "Nothing due" parks a happy sun at noon. Warm metaphor, zero numbers pressure; arc + sun is one drawn bitmap.',
    render: (s) => <ConceptSunrise s={s} />,
  },
];

export function WidgetLabPage() {
  const [scenarioId, setScenarioId] = useState('rolling');
  const scenario = SCENARIOS.find((s) => s.id === scenarioId) ?? SCENARIOS[1];

  return (
    <div className="wl-page">
      <h1>Widget lab</h1>
      <p className="wl-intro">
        Home-screen widget concepts on mock day-states — unlisted review page, not part of the app.
        No chore names by design: just the day&apos;s shape and a nudge. Pick a scenario and scroll;
        each concept shows its 4×2 and 2×2 sizes on a wallpaper. See also the{' '}
        <Link to="/design-lab">chore-row lab</Link>.
      </p>
      <div className="wl-scenarios" role="group" aria-label="Day scenario">
        {SCENARIOS.map((s) => (
          <button
            key={s.id}
            className={`choice-chip${s.id === scenarioId ? ' choice-chip-active' : ''}`}
            aria-pressed={s.id === scenarioId}
            onClick={() => {
              tapLight();
              setScenarioId(s.id);
            }}
          >
            {s.label}
          </button>
        ))}
      </div>
      {CONCEPTS.map((c) => (
        <section key={c.id} id={c.id} className="wl-concept">
          <h2 className="wl-concept-title">{c.title}</h2>
          <p className="wl-concept-blurb">{c.blurb}</p>
          <div className="wl-shelf">
            {c.render(scenario)}
            <p className="wl-size-label">4×2 · 2×2 — {scenario.label}</p>
          </div>
        </section>
      ))}
    </div>
  );
}
